import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { useVerifiedDancer } from '../hooks/useVerifiedDancer';
import { loadDancerDirectory } from '../utils/dancerDirectory';
import { getCachedDancerRankings } from '../utils/globalCache';
import { matchDancer, matchPastedLine, nameTokens } from '../utils/dancerMatching';
import PredictionBallot, { Avatar, IconX, photoNameOf } from './PredictionBallot';
import {
  PREDICTION_AGES,
  LATIN_DANCES,
  buildPredictionCategory,
  disciplinesForCard,
  sortCategories
} from '../utils/predictionCategories';
import './PredictWinner.css';

/** A category is votable by the public once it has at least two dancers on the start list. */
export const isCategoryVotable = (cat) => (cat.startlist || []).length >= 2;

/** True when the public should see the "Predict Winner" card for this event. */
export const isEventPublic = (event) =>
  !!event && ['open', 'closed', 'settled'].includes(event.status) && (event.categories || []).some(isCategoryVotable);

const STATUS_LABEL = {
  draft: 'Draft — hidden from the public',
  open: 'Open — public can vote',
  closed: 'Voting closed',
  settled: 'Results published'
};

// ─────────────────────────────────────────────────────────────────────────────
// Admin: start list editor for one category
//
// Every name is matched against the dancers in the app's database:
//   exact  → linked automatically (photo + studio come from the database dancer)
//   review → similar names found; an admin must confirm "same person" (or "none of these")
//   none   → not in the database; shown with initials only
// Pasted lists may contain numbers, studios, tabs, "Surname, Name" – the matcher finds the name in each line.
// ─────────────────────────────────────────────────────────────────────────────
const LINKED = ['exact', 'confirmed'];

const keysOf = (d) => [d.name.toLowerCase(), (d.dancerKey || '').toLowerCase()].filter(Boolean);

/** Drop entries that point at a dancer already on the list. */
const dedupeEntries = (list) => {
  const have = new Set();
  return list.filter((d) => {
    const keys = keysOf(d);
    if (keys.some((k) => have.has(k))) return false;
    keys.forEach((k) => have.add(k));
    return true;
  });
};

/** Names from the dancer database that start with what was typed (Greek or Latin letters). */
const suggestNames = (term, names, taken) => {
  const q = nameTokens(term);
  if (!q.length) return [];
  const out = [];
  for (const n of names) {
    if (taken.has(n.toLowerCase())) continue;
    const t = nameTokens(n);
    if (q.every((qt) => t.some((tt) => tt.startsWith(qt)))) {
      out.push(n);
      if (out.length >= 8) break;
    }
  }
  return out;
};

function StartlistEditor({ category, directory, onRetryDirectory, resolveEntry, onChange, onRemove }) {
  const [term, setTerm] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(null); // { kind, text }

  const suggestions = useMemo(() => {
    if (directory.state !== 'ready' || term.trim().length < 2) return [];
    const taken = new Set(category.startlist.flatMap(keysOf));
    return suggestNames(term, directory.names, taken);
  }, [term, directory, category.startlist]);

  // mode: 'known' = picked from the database list · 'typed' = one typed name · 'pasted' = lines of a start list
  const addNames = async (lines, mode = 'typed') => {
    const raw = lines.map((l) => l.trim()).filter(Boolean);
    if (!raw.length) return;
    setAdding(true);
    setNotice(null);
    try {
      const resolved = (await Promise.all(raw.map((l) => resolveEntry(l, mode)))).filter(Boolean);
      onChange((cat) => ({ ...cat, startlist: dedupeEntries([...cat.startlist, ...resolved]) }));
      const n = (match) => resolved.filter((e) => match.includes(e.match)).length;
      const parts = [`${n(LINKED)} linked to database dancers`];
      if (n(['pending'])) parts.push(`${n(['pending'])} need your approval`);
      if (n(['none'])) parts.push(`${n(['none'])} not found in the database`);
      setNotice({ kind: n(['none']) || n(['pending']) ? 'warn' : 'ok', text: `Added ${resolved.length}: ${parts.join(', ')}.` });
    } catch (e) {
      console.error('Adding names failed:', e);
      setNotice({ kind: 'error', text: 'Could not load the dancer database, so these names were not added.', retry: true });
    } finally {
      setAdding(false);
    }
  };

  const removeDancer = (name) =>
    onChange((cat) => ({ ...cat, startlist: cat.startlist.filter((d) => d.name !== name) }));

  // Admin approves: "yes, this is the same person as <candidate>"
  const confirmMatch = (entryName, candidate) => onChange((cat) => {
    const list = cat.startlist;
    const idx = list.findIndex((d) => d.name === entryName);
    if (idx < 0) return cat;
    const duplicate = list.some((d, i) => i !== idx && (d.name === candidate.name || d.dancerKey === candidate.name));
    if (duplicate) return { ...cat, startlist: list.filter((_, i) => i !== idx) };
    const next = {
      name: candidate.name,
      enteredName: list[idx].enteredName || entryName,
      dancerKey: candidate.name,
      studio: candidate.studio || null,
      match: 'confirmed'
    };
    return { ...cat, startlist: list.map((d, i) => (i === idx ? next : d)) };
  });

  const rejectMatch = (entryName) => onChange((cat) => ({
    ...cat,
    startlist: cat.startlist.map((d) => (d.name === entryName ? { name: d.name, dancerKey: null, studio: null, match: 'none' } : d))
  }));

  const dirLoading = directory.state === 'loading';
  const dirError = directory.state === 'error';

  return (
    <div className="pw-cat">
      <div className="pw-cat-head">
        <strong className="pw-cat-label">{category.label}</strong>
        <span className={`pw-count ${isCategoryVotable(category) ? 'ok' : ''}`}>
          {category.startlist.length} dancer{category.startlist.length === 1 ? '' : 's'}
        </span>
        <button className="pw-link danger" onClick={onRemove}>Remove category</button>
      </div>

      {dirLoading && <p className="pw-muted">Loading the dancer database… you can paste now — names are linked as soon as it is ready.</p>}
      {dirError && (
        <p className="pw-msg error">
          Could not load the dancer database, so names cannot be linked.{' '}
          <button className="pw-link" onClick={onRetryDirectory}>Retry</button>
        </p>
      )}

      <div className="pw-add">
        <input
          type="search"
          placeholder="Search a dancer to add…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && term.trim()) {
              if (suggestions[0]) addNames([suggestions[0]], 'known');
              else addNames([term]);
              setTerm('');
            }
          }}
        />
        {term.trim().length >= 2 && (
          <ul className="pw-suggest">
            {suggestions.map((n) => (
              <li key={n}><button onClick={() => { addNames([n], 'known'); setTerm(''); }}>{n}</button></li>
            ))}
            <li className="pw-suggest-custom">
              <button onClick={() => { addNames([term]); setTerm(''); }}>Add &ldquo;{term.trim()}&rdquo; and match it to the database</button>
            </li>
          </ul>
        )}
      </div>

      <button className="pw-link" onClick={() => setPasteOpen((v) => !v)}>
        {pasteOpen ? 'Hide paste box' : 'Paste a list of names'}
      </button>
      {pasteOpen && (
        <div className="pw-paste">
          <textarea
            rows={6}
            placeholder={'One dancer per line — numbers, studios, columns and Greek or Latin letters are fine\n1. Maria Papadopoulou    Prima Dance\nΝίκος Γεωργίου\nKaragianni, Eleni'}
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <button
            className="pw-btn"
            disabled={adding}
            onClick={async () => { await addNames(pasteText.split(/\r?\n/), 'pasted'); setPasteText(''); setPasteOpen(false); }}
          >
            {adding ? 'Matching names…' : 'Add names'}
          </button>
        </div>
      )}
      {adding && !pasteOpen && <p className="pw-muted">Matching names to the database…</p>}
      {notice && (
        <p className={`pw-msg ${notice.kind === 'ok' ? 'ok' : notice.kind === 'warn' ? 'warn' : 'error'}`}>
          {notice.text}{' '}
          {notice.retry && <button className="pw-link" onClick={onRetryDirectory}>Retry</button>}
        </p>
      )}

      <ul className="pw-startlist">
        {category.startlist.map((d) => {
          const linked = LINKED.includes(d.match);
          return (
            <li key={d.name} className={`pw-entry ${d.match || ''}`}>
              <Avatar name={d.name} photoName={photoNameOf(d)} size={30} />
              <span className="pw-entry-main">
                <span className="pw-entry-name">{d.name}</span>
                <span className="pw-entry-sub">
                  {d.studio || (d.match === 'none' ? 'Not found in database' : d.match === 'pending' ? 'Needs your approval' : !d.match ? 'Not checked yet' : ' ')}
                </span>
              </span>
              {linked && <span className="pw-badge ok" title={d.enteredName ? `Entered as “${d.enteredName}”` : 'Linked to a database dancer'}>Linked</span>}
              {d.match === 'pending' && <span className="pw-badge warn">Review</span>}
              <button className="pw-x" onClick={() => removeDancer(d.name)} aria-label={`Remove ${d.name}`}>✕</button>

              {d.match === 'pending' && (
                <div className="pw-review">
                  <p>Is <strong>{d.name}</strong> the same person as…</p>
                  <div className="pw-review-options">
                    {(d.candidates || []).map((c) => (
                      <button key={c.name} className="pw-review-option" onClick={() => confirmMatch(d.name, c)}>
                        <Avatar name={c.name} size={24} />
                        <strong>{c.name}</strong>
                        <small>{c.studio ? `${c.studio} · ` : ''}{Math.round(c.score * 100)}% similar</small>
                      </button>
                    ))}
                    <button className="pw-review-none" onClick={() => rejectMatch(d.name)}>None of these — keep as typed</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin: manage start lists, publish, close and settle
// ─────────────────────────────────────────────────────────────────────────────
export function AdminPanel({ competition, event }) {
  const [cats, setCats] = useState(() => (event ? event.categories || [] : []));
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState('saved'); // saved | pending | saving | error
  const [saveError, setSaveError] = useState(null);
  const [directory, setDirectory] = useState({ state: 'loading', names: [] }); // dancer database for matching
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const disciplines = useMemo(() => disciplinesForCard(competition), [competition]);
  const [builder, setBuilder] = useState({ discipline: disciplines[0], age: 'Adult', dance: 'ALL' });
  const [preview, setPreview] = useState(null); // { competitionName, candidates, categories }
  const [chosenComp, setChosenComp] = useState('');

  const loadDirectory = useCallback(async () => {
    setDirectory((d) => ({ ...d, state: 'loading' }));
    try {
      const dir = await loadDancerDirectory();
      setDirectory({ state: 'ready', names: dir.names });
    } catch (e) {
      console.error('Dancer database failed to load:', e);
      setDirectory((d) => ({ ...d, state: 'error' }));
    }
  }, []);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  const studioCache = useRef(new Map());

  // current studio of a database dancer (stored on the entry so the public needs no extra reads)
  const lookupStudio = useCallback(async (name) => {
    if (studioCache.current.has(name)) return studioCache.current.get(name);
    let studio = null;
    try {
      const data = await getCachedDancerRankings(name);
      const s = data && data.studio;
      studio = s && s !== '-' && s !== 'No Studio' ? s : null;
    } catch (e) {
      console.warn('Studio lookup failed for', name, e);
    }
    studioCache.current.set(name, studio);
    return studio;
  }, []);

  // One dancer stored under several spellings (Greek + Latin letters, capitals…): show the one that
  // has a profile picture, else a claimed profile, else the Latin-letter spelling.
  const pickSpelling = useCallback(async (names) => {
    if (names.length <= 1) return names[0];
    const info = await Promise.all(names.map(async (name) => {
      try {
        const snap = await getDoc(doc(db, 'dancerProfiles', name));
        const d = snap.exists() ? snap.data() : {};
        return { name, pic: d.profilePicture ? 1 : 0, claimed: d.profileClaimed ? 1 : 0 };
      } catch (e) {
        return { name, pic: 0, claimed: 0 };
      }
    }));
    const greek = (n) => (/[\u0370-\u03ff]/.test(n) ? 1 : 0);
    info.sort((x, y) => (y.pic - x.pic) || (y.claimed - x.claimed) || (greek(x.name) - greek(y.name)) || x.name.localeCompare(y.name));
    return info[0].name;
  }, []);

  // typed / pasted name → start-list entry linked to the database dancer (or flagged for review).
  // Waits for the dancer database, so a list pasted right after opening is never matched against nothing.
  const resolveEntry = useCallback(async (raw, mode = 'typed') => {
    if (mode === 'known') return { name: raw, dancerKey: raw, studio: await lookupStudio(raw), match: 'exact' };
    const dir = await loadDancerDirectory();
    const m = mode === 'pasted' ? matchPastedLine(raw, dir.index) : matchDancer(raw, dir.index);
    if (!m) return null;
    const typed = (m.typed || raw).trim();
    if (m.status === 'exact') {
      const chosen = await pickSpelling(m.twins || [m.match]);
      const entry = { name: chosen, dancerKey: chosen, studio: await lookupStudio(chosen), match: 'exact' };
      if (chosen !== typed) entry.enteredName = typed;
      return entry;
    }
    if (m.status === 'review') {
      const candidates = await Promise.all(m.candidates.map(async (c) => ({
        name: c.name, score: c.score, studio: await lookupStudio(c.name)
      })));
      return { name: typed, dancerKey: null, studio: null, match: 'pending', candidates };
    }
    return { name: typed, dancerKey: null, studio: null, match: 'none' };
  }, [lookupStudio, pickSpelling]);

  // ── Saving ───────────────────────────────────────────────────────────────
  // Every edit is saved automatically shortly after it is made, and any pending edit is written when the
  // panel is closed or the tab is switched – nothing depends on remembering to press a Save button.
  const catsRef = useRef(cats);
  catsRef.current = cats;
  const eventRef = useRef(event);
  eventRef.current = event;
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const createdRef = useRef(false); // the event document exists (or has just been created)

  const markDirty = () => {
    dirtyRef.current = true;
    setDirty(true);
    setSaveState((st) => (st === 'saving' ? st : 'pending'));
  };

  const friendlyError = (e) => (e && e.code === 'permission-denied'
    ? 'Not allowed to save — sign in with your verified admin email.'
    : `Could not save (${(e && (e.code || e.message)) || 'unknown error'}).`);

  // Writes the start lists. Only a status change (publish / close / hide) sends a status.
  const writeCategories = useCallback(async (snapshot, nextStatus) => {
    const isNew = !eventRef.current && !createdRef.current;
    const payload = {
      competitionId: competition.id,
      competitionName: competition.name,
      competitionDate: competition.date || null,
      updatedAt: serverTimestamp(),
      // Firestore rejects undefined values; a JSON round trip drops them
      categories: JSON.parse(JSON.stringify(snapshot))
    };
    if (nextStatus) payload.status = nextStatus;
    else if (isNew) payload.status = 'draft';
    if (isNew) payload.createdAt = serverTimestamp();
    await setDoc(doc(db, 'predictionEvents', competition.id), payload, { merge: true });
    createdRef.current = true;
  }, [competition.id, competition.name, competition.date]);
  const writeRef = useRef(writeCategories);
  writeRef.current = writeCategories;

  const persistRef = useRef(null);
  const persist = useCallback(async () => {
    if (savingRef.current || !dirtyRef.current) return;
    savingRef.current = true;
    const snapshot = catsRef.current;
    if (mountedRef.current) { setSaveState('saving'); setSaveError(null); }
    try {
      await writeRef.current(snapshot);
      if (catsRef.current === snapshot) {
        dirtyRef.current = false;
        if (mountedRef.current) { setDirty(false); setSaveState('saved'); }
      } else if (mountedRef.current) {
        setSaveState('pending'); // edited while saving – the next autosave picks it up
        setTimeout(() => persistRef.current && persistRef.current(), 250);
      } else {
        await writeRef.current(catsRef.current); // panel closed mid-save: write the latest edits too
        dirtyRef.current = false;
      }
    } catch (e) {
      console.error('Saving the start lists failed:', e);
      if (mountedRef.current) { setSaveError(friendlyError(e)); setSaveState('error'); }
    } finally {
      savingRef.current = false;
    }
  }, []);
  persistRef.current = persist;

  // autosave: shortly after the last edit
  useEffect(() => {
    if (!dirty) return undefined;
    const t = setTimeout(() => persistRef.current(), 600);
    return () => clearTimeout(t);
  }, [cats, dirty]);

  // closing the panel / switching tab: write whatever is still pending
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (dirtyRef.current && !savingRef.current) {
        writeRef.current(catsRef.current).catch((e) => console.error('Saving on close failed:', e));
      }
    };
  }, []);

  // Pick up changes made elsewhere (another admin), but never overwrite edits that are not saved yet
  useEffect(() => {
    if (!event || dirtyRef.current || savingRef.current) return;
    const server = JSON.stringify(event.categories || []);
    if (server !== JSON.stringify(catsRef.current)) setCats(event.categories || []);
  }, [event, dirty, saveState]);

  const status = event ? event.status : 'draft';
  const flash = (kind, text) => setMsg({ kind, text });
  const pendingCount = cats.reduce((n, c) => n + c.startlist.filter((d) => d.match === 'pending').length, 0);

  // publish / close / hide: saves the current start lists together with the new status
  const saveCategories = async (nextStatus) => {
    if (nextStatus === 'open' && pendingCount > 0 &&
        !window.confirm(`${pendingCount} dancer name${pendingCount === 1 ? ' still needs' : 's still need'} your approval and will show without a photo. Publish anyway?`)) {
      return;
    }
    setBusy(true);
    try {
      const snapshot = catsRef.current;
      await writeCategories(snapshot, nextStatus);
      if (catsRef.current === snapshot) {
        dirtyRef.current = false;
        setDirty(false);
        setSaveState('saved');
      }
      flash('ok', `Status: ${STATUS_LABEL[nextStatus]}`);
    } catch (e) {
      console.error(e);
      flash('error', friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const addCategory = () => {
    const dances = builder.discipline === 'Latin'
      ? (builder.dance === 'ALL' ? LATIN_DANCES : [builder.dance])
      : [null];
    const existing = new Set(catsRef.current.map((c) => c.id));
    const additions = dances
      .map((d) => buildPredictionCategory(builder.discipline, builder.age, d))
      .filter((c) => !existing.has(c.id));
    if (additions.length === 0) {
      flash('error', 'Those categories are already added.');
      return;
    }
    setCats((current) => sortCategories([...current, ...additions]));
    markDirty();
    setMsg(null);
  };

  const updateCategory = (id, next) => {
    setCats((current) => current.map((c) => (c.id === id ? (typeof next === 'function' ? next(c) : next) : c)));
    markDirty();
  };

  const unlinkedCount = cats.reduce((n, c) => n + c.startlist.filter((d) => !LINKED.includes(d.match)).length, 0);

  // Re-run the matching for every entry that is not linked yet (e.g. saved before the database loaded)
  const relinkAll = async () => {
    setBusy(true);
    setMsg(null);
    try {
      let linked = 0;
      const next = [];
      for (const c of catsRef.current) {
        const list = [];
        for (const d of c.startlist) {
          if (LINKED.includes(d.match)) { list.push(d); continue; }
          const entry = await resolveEntry(d.enteredName || d.name, 'pasted');
          if (entry && LINKED.includes(entry.match)) linked++;
          list.push(entry || d);
        }
        next.push({ ...c, startlist: dedupeEntries(list) });
      }
      setCats(next);
      markDirty();
      flash(linked ? 'ok' : 'error', linked
        ? `${linked} name${linked === 1 ? ' is' : 's are'} now linked to database dancers (saved automatically).`
        : 'No more names could be linked automatically — open the highlighted entries to approve or add them.');
    } catch (e) {
      console.error(e);
      flash('error', 'Could not load the dancer database. Please retry.');
    } finally {
      setBusy(false);
    }
  };

  const removeCategory = (id) => {
    setCats((current) => current.filter((c) => c.id !== id));
    markDirty();
  };

  // Look up the winners in the uploaded results (nothing is typed in by hand)
  const checkResults = async (name) => {
    setBusy(true);
    setMsg(null);
    try {
      const fn = httpsCallable(getFunctions(), 'previewPredictionResults');
      const res = await fn({ eventId: competition.id, competitionName: name || undefined });
      setPreview(res.data);
      setChosenComp(res.data.competitionName || '');
    } catch (e) {
      console.error(e);
      flash('error', e.message || 'Could not read the uploaded results.');
    } finally {
      setBusy(false);
    }
  };

  const foundCount = preview ? preview.categories.filter((c) => c.status === 'found').length : 0;

  const settle = async () => {
    const verb = status === 'settled' ? 'Re-publish' : 'Publish';
    if (!window.confirm(`${verb} results from "${chosenComp}" for ${foundCount} categor${foundCount === 1 ? 'y' : 'ies'} and award prediction points?`)) return;
    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(), 'settlePredictions');
      const res = await fn({ eventId: competition.id, competitionName: chosenComp });
      const d = res.data || {};
      const pending = d.categoriesPending
        ? `; ${d.categoriesPending} categor${d.categoriesPending === 1 ? 'y is' : 'ies are'} still waiting for results`
        : '';
      flash('ok', `Results published. ${d.predictions || 0} predictions scored, ${d.dancersAwarded || 0} verified dancers got points${pending}.`);
      await checkResults(chosenComp);
    } catch (e) {
      console.error(e);
      flash('error', e.message || 'Could not publish results.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pw-admin">
      <div className="pw-statusbar">
        <span className={`pw-status ${status}`}>{STATUS_LABEL[status]}</span>
        {event && status !== 'settled' && (
          <div className="pw-status-actions">
            {status !== 'open' && (
              <button className="pw-btn" disabled={busy} onClick={() => saveCategories('open')}>
                {status === 'closed' ? 'Re-open voting' : 'Publish to public'}
              </button>
            )}
            {status === 'open' && (
              <button className="pw-btn secondary" disabled={busy} onClick={() => saveCategories('closed')}>
                Close voting
              </button>
            )}
            {status !== 'draft' && (
              <button className="pw-btn secondary" disabled={busy} onClick={() => saveCategories('draft')}>
                Hide (draft)
              </button>
            )}
          </div>
        )}
      </div>
      <div className={`pw-savestate ${saveState}`} role="status" aria-live="polite">
        {saveState === 'saved' && <>✓ All changes saved</>}
        {(saveState === 'pending' || saveState === 'saving') && <>Saving…</>}
        {saveState === 'error' && (
          <>
            ⚠ {saveError || 'Not saved.'}{' '}
            <button className="pw-link" onClick={() => persistRef.current()}>Retry</button>
          </>
        )}
      </div>
      {pendingCount > 0 && (
        <p className="pw-warn">
          {pendingCount} dancer name{pendingCount === 1 ? ' needs' : 's need'} your approval — open the highlighted
          entries below and confirm whether they are the same person as a database dancer.
        </p>
      )}
      {msg && <p className={`pw-msg ${msg.kind}`}>{msg.text}</p>}

      <h4>Start lists</h4>
      <p className="pw-help">
        Add the dancers of each category. A category appears to the public once it has at least 2 dancers
        and the event is published.
      </p>
      {unlinkedCount > 0 && (
        <p className="pw-warn">
          {unlinkedCount} name{unlinkedCount === 1 ? ' is' : 's are'} not linked to a dancer yet, so they show without a photo or studio.{' '}
          <button className="pw-link" disabled={busy} onClick={relinkAll}>Re-check dancer links</button>
        </p>
      )}

      <div className="pw-builder">
        <select value={builder.discipline} onChange={(e) => setBuilder({ ...builder, discipline: e.target.value })} aria-label="Discipline">
          {disciplines.map((d) => <option key={d}>{d}</option>)}
        </select>
        <select value={builder.age} onChange={(e) => setBuilder({ ...builder, age: e.target.value })} aria-label="Age category">
          {PREDICTION_AGES.map((a) => <option key={a}>{a}</option>)}
        </select>
        {builder.discipline === 'Latin' && (
          <select value={builder.dance} onChange={(e) => setBuilder({ ...builder, dance: e.target.value })} aria-label="Dance">
            <option value="ALL">All 5 dances</option>
            {LATIN_DANCES.map((d) => <option key={d}>{d}</option>)}
          </select>
        )}
        <button className="pw-btn" onClick={addCategory}>Add category</button>
      </div>

      {cats.length === 0 && <p className="pw-muted">No categories yet — add one above.</p>}
      {cats.map((c) => (
        <StartlistEditor
          key={c.id}
          category={c}
          directory={directory}
          onRetryDirectory={loadDirectory}
          resolveEntry={resolveEntry}
          onChange={(next) => updateCategory(c.id, next)}
          onRemove={() => removeCategory(c.id)}
        />
      ))}

      <div className="pw-save-row">
        <button className="pw-btn secondary" disabled={saveState === 'saved' || saveState === 'saving'} onClick={() => persistRef.current()}>
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : 'Save now'}
        </button>
        <span className="pw-muted">Changes are saved automatically.</span>
      </div>

      {event && cats.some(isCategoryVotable) && status !== 'draft' && (
        <div className="pw-settle">
          <h4>Results</h4>
          <p className="pw-help">
            Winners are taken automatically from the results you upload (1st place of each category). When the
            uploaded results of this competition contain every category, the predictions are scored by themselves.
            You can also check and publish them here.
          </p>
          <button className="pw-btn secondary" disabled={busy || dirty} onClick={() => checkResults(chosenComp)}>
            {busy && !preview ? 'Checking…' : 'Check uploaded results'}
          </button>

          {preview && (
            <div className="pw-results">
              {preview.candidates.length > 0 && (
                <label className="pw-results-pick">
                  Results from uploaded competition:
                  <select
                    value={chosenComp}
                    onChange={(e) => { setChosenComp(e.target.value); checkResults(e.target.value); }}
                  >
                    {!chosenComp && <option value="">— choose —</option>}
                    {preview.candidates.map((c) => (
                      <option key={c.name} value={c.name}>{c.name}{c.exact ? ' ✓ name match' : ''}</option>
                    ))}
                  </select>
                </label>
              )}
              {preview.candidates.length === 0 && <p className="pw-muted">No recently uploaded competition results found yet.</p>}
              {!preview.competitionName && preview.candidates.length > 0 && (
                <p className="pw-warn">No exact name match — choose the uploaded competition above.</p>
              )}
              <ul className="pw-results-list">
                {preview.categories.filter((c) => c.status !== 'skipped').map((c) => (
                  <li key={c.id} className={c.status}>
                    <span className="pw-results-cat">{c.label}</span>
                    <span className="pw-results-out">
                      {c.status === 'found' && <>{c.winner}</>}
                      {c.status === 'missing' && <>Results not uploaded yet</>}
                      {c.status === 'ambiguous' && <>Several 1st places: {c.candidates.join(', ')}</>}
                      {c.status === 'not-on-startlist' && <>Winner {c.winner} is not on the start list</>}
                    </span>
                  </li>
                ))}
              </ul>
              <button className="pw-btn gold" disabled={busy || dirty || !chosenComp || foundCount === 0} onClick={settle}>
                {busy ? 'Publishing…' : status === 'settled' ? 'Re-publish results' : `Publish ${foundCount} categor${foundCount === 1 ? 'y' : 'ies'} & award points`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Public: vote panel (data + persistence; the UI lives in PredictionBallot)
// ─────────────────────────────────────────────────────────────────────────────
const cleanPicks = (picks, categories) => {
  const out = {};
  categories.forEach((c) => { if (picks && picks[c.id]) out[c.id] = picks[c.id]; });
  return out;
};
const samePicks = (a, b) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

function VotePanel({ competition, event, user, isVerified, verifiedDancerName, previewAsAdmin, readOnly }) {
  const navigate = useNavigate();
  const [picks, setPicks] = useState({});
  const [saved, setSaved] = useState(null); // my prediction doc
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [insights, setInsights] = useState({ state: 'idle', data: null }); // admin only

  const loadInsights = useCallback(async () => {
    setInsights((s) => ({ ...s, state: 'loading' }));
    try {
      const res = await httpsCallable(getFunctions(), 'predictionInsights')({ eventId: competition.id });
      setInsights({ state: 'ready', data: res.data });
    } catch (e) {
      console.error('Win probability failed:', e);
      const message = e && e.code === 'functions/not-found'
        ? 'The win-probability function is not deployed yet — deploy the latest Cloud Functions.'
        : e && e.code === 'functions/permission-denied'
          ? 'Admin access is required (sign in with your verified admin email).'
          : (e && e.message) || 'Could not calculate.';
      setInsights((s) => ({ ...s, state: 'error', message }));
    }
  }, [competition.id]);

  useEffect(() => {
    if (previewAsAdmin) loadInsights();
  }, [previewAsAdmin, loadInsights]);

  const votable = (event.categories || []).filter(isCategoryVotable);
  const settled = event.status === 'settled';
  const open = event.status === 'open';
  const phase = settled ? 'settled' : open ? 'open' : 'closed';
  const predictionId = user ? `${competition.id}_${user.uid}` : null;

  useEffect(() => {
    if (!activeCat && votable.length) setActiveCat(votable[0].id);
  }, [votable, activeCat]);

  // Load my saved prediction
  useEffect(() => {
    if (!predictionId) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'predictions', predictionId))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        setSaved(snap.data());
        setPicks(snap.data().picks || {});
      })
      .catch((e) => console.error('Could not load prediction:', e));
    return () => { cancelled = true; };
  }, [predictionId, settled]);

  // Vote counts (live)
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'predictionStats', competition.id), (snap) => {
      setStats(snap.exists() ? snap.data() : null);
    }, () => {});
    return unsub;
  }, [competition.id]);

  const savedPicks = cleanPicks(saved && saved.picks, votable);
  const currentPicks = cleanPicks(picks, votable);
  const hasVoted = Object.keys(savedPicks).length > 0;
  const dirty = !samePicks(currentPicks, savedPicks);
  const saveState = saving ? 'saving' : failed && dirty ? 'error' : dirty ? 'dirty' : hasVoted ? 'saved' : 'clean';

  const getPercent = (catId, name) => {
    const counts = (stats && stats.counts && stats.counts[catId]) || {};
    const total = Object.values(counts).reduce((a, b) => a + Math.max(0, b), 0);
    if (!total) return null;
    return Math.round((Math.max(0, counts[name] || 0) / total) * 100);
  };

  const submit = async () => {
    if (!user) { navigate('/login'); return; }
    setSaving(true);
    setFailed(false);
    try {
      const payload = {
        eventId: competition.id,
        userId: user.uid,
        displayName: user.displayName || null,
        picks: currentPicks,
        updatedAt: serverTimestamp()
      };
      if (!saved) payload.createdAt = serverTimestamp();
      await setDoc(doc(db, 'predictions', predictionId), payload, { merge: true });
      setSaved({ ...(saved || {}), picks: currentPicks });
    } catch (e) {
      console.error(e);
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  if (votable.length === 0) {
    return <div className="pw-scroll"><p className="pw-empty">No categories are open for predictions yet.</p></div>;
  }

  // Result summary once the event is settled
  let score = null;
  if (settled) {
    const scored = votable.filter((c) => c.winner && savedPicks[c.id]);
    const correct = scored.filter((c) => savedPicks[c.id] === c.winner).length;
    score = {
      scored: scored.length,
      correct,
      pointsNode: saved && saved.verifiedDancerName && correct > 0 ? (
        <Link className="pw-result-link" to={`/dancer/${encodeURIComponent(saved.verifiedDancerName)}`}>
          +{correct} prediction point{correct > 1 ? 's' : ''} added to your profile
        </Link>
      ) : null
    };
  }

  return (
    <PredictionBallot
      categories={votable}
      activeId={activeCat}
      onSelectCategory={setActiveCat}
      picks={currentPicks}
      savedPicks={savedPicks}
      onPick={(catId, name) => { setFailed(false); setPicks({ ...picks, [catId]: name }); }}
      phase={phase}
      interactive={open && !!user && !readOnly}
      signedIn={!!user}
      onSignIn={() => navigate('/login')}
      getPercent={getPercent}
      showPercents={settled || hasVoted || !open}
      voters={stats && stats.voters}
      verifiedName={isVerified ? verifiedDancerName : null}
      score={score}
      saveState={saveState}
      hasVoted={hasVoted}
      onSubmit={submit}
      previewNote={previewAsAdmin
        ? `Admin view of the public screen${readOnly ? ' — voting is disabled while the event is a draft' : ''}.`
        : null}
      adminInsights={previewAsAdmin ? { state: insights.state, data: insights.data, message: insights.message, onRefresh: loadInsights } : null}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal
// ─────────────────────────────────────────────────────────────────────────────
const PHASE_CHIP = {
  open: { text: 'Voting open', tone: 'open' },
  closed: { text: 'Voting closed', tone: 'closed' },
  settled: { text: 'Final results', tone: 'settled' },
  draft: { text: 'Draft', tone: 'draft' }
};

export default function PredictWinnerModal({ competition, onClose }) {
  const { user, isAdmin, isVerified, verifiedDancerName, loading } = useVerifiedDancer();
  const [event, setEvent] = useState(undefined); // undefined = loading, null = none
  const [view, setView] = useState('manage'); // admin only: manage | preview

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'predictionEvents', competition.id),
      (snap) => setEvent(snap.exists() ? snap.data() : null),
      (err) => { console.error('Prediction event listener failed:', err); setEvent(null); }
    );
    return unsub;
  }, [competition.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const publicVisible = isEventPublic(event);
  const chip = event ? PHASE_CHIP[event.status] : null;

  let body;
  if (event === undefined || loading) {
    body = (
      <div className="pw-scroll" aria-busy="true">
        <div className="pw-skeleton" style={{ height: 18, width: '38%' }} />
        <div className="pw-skeleton" style={{ height: 64, marginTop: 20 }} />
        <div className="pw-skeleton" style={{ height: 64, marginTop: 10 }} />
        <div className="pw-skeleton" style={{ height: 64, marginTop: 10 }} />
      </div>
    );
  } else if (isAdmin) {
    body = (
      <>
        <div className="pw-segmented" role="tablist" aria-label="Admin view">
          <button role="tab" aria-selected={view === 'manage'} className={view === 'manage' ? 'active' : ''} onClick={() => setView('manage')}>Manage</button>
          <button role="tab" aria-selected={view === 'preview'} className={view === 'preview' ? 'active' : ''} onClick={() => setView('preview')} disabled={!event}>Voting view</button>
        </div>
        {view === 'manage'
          ? <div className="pw-scroll"><AdminPanel competition={competition} event={event} /></div>
          : event && (
            <VotePanel
              competition={competition}
              event={{ ...event, status: event.status === 'draft' ? 'open' : event.status }}
              user={user}
              isVerified={isVerified}
              verifiedDancerName={verifiedDancerName}
              previewAsAdmin
              readOnly={event.status === 'draft'}
            />
          )}
      </>
    );
  } else if (!publicVisible) {
    body = (
      <div className="pw-scroll">
        <p className="pw-empty">Predictions for this competition are not open yet. Check back once the start lists are published.</p>
      </div>
    );
  } else {
    body = (
      <VotePanel
        competition={competition}
        event={event}
        user={user}
        isVerified={isVerified}
        verifiedDancerName={verifiedDancerName}
      />
    );
  }

  return createPortal(
    <div className="pw-root pw-overlay" onClick={onClose} role="presentation">
      <div className="pw-modal" role="dialog" aria-modal="true" aria-labelledby="pw-title" onClick={(e) => e.stopPropagation()}>
        <header className="pw-modal-head">
          <div className="pw-modal-titles">
            <span className="pw-eyebrow">Predict Winner</span>
            <h3 id="pw-title">{competition.name}</h3>
            {competition.date && <span className="pw-modal-sub">{competition.date}</span>}
          </div>
          <div className="pw-modal-actions">
            {chip && <span className={`pw-chip-status ${chip.tone}`}><i />{chip.text}</span>}
            <button className="pw-close" onClick={onClose} aria-label="Close"><IconX size={18} /></button>
          </div>
        </header>
        <div className="pw-modal-body">{body}</div>
      </div>
    </div>,
    document.body
  );
}
